import {
  MATCH_COPY_SPORT_OPTIONS,
  MATCH_COPY_STYLE_OPTIONS,
  MATCH_COPY_VARIANT_OPTIONS,
  buildMatchCopyContext,
  buildMatchCopyLookupPaths,
  buildMatchCopyLookupRoots,
  getMatchCopyScopedPath,
  type MatchCopyContext,
  type MatchCopyScope,
  type MatchCopyStyle,
} from '../shared/matchCopyContext';
import {
  PLAY_MODE_PICKER_COPY_DEFAULTS,
  type PlayModePickerCopySettings,
} from '../shared/playModePickerCopy';

export type PlayModeCopyField = {
  path: string;
  label: string;
  section: 'sheet' | 'quick_play' | 'individuals' | 'teams' | 'score_challenge' | 'tournament';
  help?: string;
};

export type PlayModeCopyPreviewContext = MatchCopyContext;
export type PlayModeCopyEditorScope = MatchCopyScope;
export type { MatchCopyStyle };

export const PLAY_MODE_COPY_DEFAULTS: PlayModePickerCopySettings = PLAY_MODE_PICKER_COPY_DEFAULTS;

export const PLAY_MODE_COPY_FIELDS: PlayModeCopyField[] = [
  { path: 'sheet.title', label: 'Sheet title', section: 'sheet' },
  { path: 'sheet.subtitle', label: 'Sheet subtitle', section: 'sheet' },
  { path: 'modes.quick_play.title', label: 'Head to Head title', section: 'quick_play' },
  { path: 'modes.quick_play.description', label: 'Head to Head description', section: 'quick_play' },
  { path: 'modes.individuals.title', label: 'Free-for-All title', section: 'individuals' },
  { path: 'modes.individuals.description', label: 'Free-for-All description', section: 'individuals' },
  { path: 'modes.teams.title', label: 'Teams title', section: 'teams' },
  { path: 'modes.teams.description', label: 'Teams description', section: 'teams' },
  { path: 'modes.score_challenge.title', label: 'Solo title', section: 'score_challenge' },
  { path: 'modes.score_challenge.description', label: 'Solo description', section: 'score_challenge' },
  { path: 'modes.tournament.title', label: 'Tournament title', section: 'tournament' },
  { path: 'modes.tournament.description', label: 'Tournament description', section: 'tournament' },
];

export const PLAY_MODE_COPY_STYLE_EDITOR_OPTIONS = MATCH_COPY_STYLE_OPTIONS;
export const PLAY_MODE_COPY_SPORT_EDITOR_OPTIONS = MATCH_COPY_SPORT_OPTIONS;
export const PLAY_MODE_COPY_VARIANT_EDITOR_OPTIONS = MATCH_COPY_VARIANT_OPTIONS;

export const PLAY_MODE_COPY_PREVIEW_SAMPLE_VARS = {
  copyStyle: 'game',
  sportId: 'soccer',
  variant: null,
  activityLabel: 'Soccer',
  activityQuestionPhrase: 'playing soccer',
  activityPlanPhrase: 'a game',
  activityPrimaryCta: 'Play Soccer',
};

export function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function deepMerge(base: unknown, override: unknown): unknown {
  if (!isObject(base) || !isObject(override)) return override ?? base;
  const out: Record<string, unknown> = { ...base };
  Object.entries(override).forEach(([key, value]) => {
    out[key] = deepMerge(base[key], value);
  });
  return out;
}

export function deepDiff(base: unknown, value: unknown): unknown | undefined {
  if (value === undefined) return undefined;
  if (!isObject(base) || !isObject(value)) return value === base ? undefined : value;
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(base), ...Object.keys(value)]);
  keys.forEach((key) => {
    const next = deepDiff(base[key], value[key]);
    if (next !== undefined) out[key] = next as unknown;
  });
  return Object.keys(out).length ? out : undefined;
}

export function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function getPathValue(source: unknown, path: string): unknown {
  const parts = path.split('.').filter(Boolean);
  let current: unknown = source;
  for (const part of parts) {
    if (!isObject(current)) return undefined;
    current = current[part];
  }
  return current;
}

export function setPathValue(target: Record<string, unknown>, path: string, value: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

export function deletePathValue(target: Record<string, unknown>, path: string) {
  const parts = path.split('.').filter(Boolean);
  if (!parts.length) return;
  let current: Record<string, unknown> = target;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const part = parts[index];
    if (!isObject(current[part])) return;
    current = current[part] as Record<string, unknown>;
  }
  delete current[parts[parts.length - 1]];
}

export function normalizePlayModePickerOverrides(value: Record<string, unknown>) {
  const merged = deepMerge(PLAY_MODE_COPY_DEFAULTS, value);
  return (deepDiff(PLAY_MODE_COPY_DEFAULTS, merged) as Record<string, unknown> | undefined) ?? {};
}

export function getContextualPathValue(
  source: unknown,
  path: string,
  context?: Partial<PlayModeCopyPreviewContext> | null
): unknown {
  const candidates = buildMatchCopyLookupPaths(path, context);
  for (const candidate of candidates) {
    const value = getPathValue(source, candidate);
    if (value !== undefined) return value;
  }
  return undefined;
}

export function getEffectivePlayModePickerCopy(
  overrides: Record<string, unknown>,
  context?: Partial<PlayModeCopyPreviewContext> | null
) {
  const effective = deepMerge(PLAY_MODE_COPY_DEFAULTS, overrides) as Record<string, unknown>;
  if (!context) return effective;
  const resolvedContext = buildMatchCopyContext(context);
  let merged = effective;
  [...buildMatchCopyLookupRoots(resolvedContext)].reverse().forEach((candidate) => {
    const scopedValue = getPathValue(effective, candidate);
    if (isObject(scopedValue)) merged = deepMerge(merged, scopedValue) as Record<string, unknown>;
  });
  return merged;
}

export function textValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

export function countOverridesForFields(
  overrides: Record<string, unknown>,
  fields: PlayModeCopyField[],
  scope: PlayModeCopyEditorScope = { kind: 'base' }
) {
  return fields.filter((field) => textValue(getPathValue(overrides, getMatchCopyScopedPath(field.path, scope))).trim().length > 0).length;
}

export function resolveScopedFieldPath(path: string, scope: PlayModeCopyEditorScope) {
  return getMatchCopyScopedPath(path, scope);
}

export function fieldsForSection(section: PlayModeCopyField['section']) {
  return PLAY_MODE_COPY_FIELDS.filter((field) => field.section === section);
}

export function sectionLabel(section: PlayModeCopyField['section']) {
  switch (section) {
    case 'sheet':
      return 'Sheet framing';
    case 'quick_play':
      return 'Head to Head';
    case 'individuals':
      return 'Free-for-All';
    case 'teams':
      return 'Teams (flagged)';
    case 'score_challenge':
      return 'Solo';
    case 'tournament':
      return 'Tournament';
    default:
      return section;
  }
}
